import type { ValidateFunction } from "ajv";
import type { ValidationError } from "../schemas/index.js";

/**
 * Configuration for the API client
 */
export interface ApiClientConfig {
  /** Base URL for the API (e.g., https://api.omnara.com) */
  baseUrl: string;
  /** Function to retrieve the current access token */
  getAccessToken: () => Promise<string | null>;
  /** Optional function to refresh the access token */
  refreshAccessToken?: () => Promise<string | null>;
  /** Optional function to handle 401 errors (e.g., sign out user) */
  onUnauthorized?: () => Promise<void>;
  /**
   * Optional delegate that replaces the built-in HTTP request logic.
   * When provided, all calls to `request()` in domain clients are
   * forwarded to this function (which may add tracing, contextual
   * wide events, etc.) instead of making direct HTTP calls.
   */
  requestDelegate?: <T>(
    path: string,
    init?: RequestInit,
    validator?: ValidateFunction<T>,
  ) => Promise<T>;
}

/**
 * Error thrown when API validation fails
 */
export class ApiValidationError extends Error {
  constructor(
    message: string,
    public readonly errors: ValidationError[],
  ) {
    super(message);
    this.name = "ApiValidationError";
  }
}

/**
 * Base HTTP error with status metadata
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly statusText: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Error thrown when payment is required (HTTP 402)
 */
export class PaymentRequiredError extends ApiError {
  constructor(message: string, details?: unknown) {
    super(message, 402, "Payment Required", details);
  }
}

/**
 * Base API client class with authentication and validation
 */
export class BaseApiClient {
  constructor(protected config: ApiClientConfig) {}

  /**
   * Get the access token for the current session
   * Exposed publicly so hooks can access it
   */
  public async getAccessToken(): Promise<string | null> {
    return this.config.getAccessToken();
  }

  /**
   * Build full URL from path
   */
  protected buildUrl(path: string): string {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    const base = this.config.baseUrl;
    if (path.startsWith("/")) {
      return `${base}${path}`;
    }
    return `${base}/${path}`;
  }

  /**
   * Parse error message from response
   */
  protected async parseError(response: Response): Promise<string> {
    try {
      const data = await response.json();
      if (typeof data === "string") {
        return data;
      }
      return data?.message || data?.detail || response.statusText;
    } catch {
      return response.statusText;
    }
  }

  /**
   * Parse error data from response for structured error handling
   */
  protected async parseErrorData(
    response: Response,
  ): Promise<{ message: string; data?: unknown }> {
    try {
      const data = await response.json();
      if (typeof data === "string") {
        return { message: data };
      }
      const message = data?.message || data?.detail || response.statusText;
      return { message, data };
    } catch {
      return { message: response.statusText };
    }
  }

  /**
   * Make an authenticated request.
   * If a requestDelegate is configured, all domain client calls are forwarded
   * to it (enabling tracing/contextual wrappers without code duplication).
   */
  protected async request<T>(
    path: string,
    init?: RequestInit,
    validator?: ValidateFunction<T>,
  ): Promise<T> {
    if (this.config.requestDelegate) {
      return this.config.requestDelegate<T>(path, init, validator);
    }

    const token = await this.config.getAccessToken();
    const url = this.buildUrl(path);

    let response = await this.fetchWithToken(url, init, token);

    if (response.status === 401 && this.config.refreshAccessToken) {
      const refreshedToken = await this.config.refreshAccessToken();
      if (refreshedToken) {
        response = await this.fetchWithToken(url, init, refreshedToken);
      }
    }

    if (!response.ok) {
      if (response.status === 401 && this.config.onUnauthorized) {
        await this.config.onUnauthorized();
      }

      // Handle 402 Payment Required with structured error data
      if (response.status === 402) {
        const { message, data } = await this.parseErrorData(response);
        throw new PaymentRequiredError(message, data);
      }

      const errorMessage = await this.parseError(response);
      throw new ApiError(errorMessage, response.status, response.statusText);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    const data = await response.json();

    // Validate response if validator provided
    if (validator) {
      if (!validator(data)) {
        throw new ApiValidationError(
          "API response validation failed",
          validator.errors as ValidationError[],
        );
      }
    }

    return data as T;
  }

  private async fetchWithToken(
    url: string,
    init: RequestInit | undefined,
    token: string | null,
  ): Promise<Response> {
    const headers = new Headers(init?.headers);

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (init?.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    return fetch(url, {
      ...init,
      headers,
    });
  }

  /**
   * Make an authenticated request with token override.
   * When a requestDelegate is configured, the explicit token is injected
   * as an Authorization header so the delegate (traced/contextual client)
   * propagates it.
   */
  protected async requestWithToken<T>(
    token: string,
    path: string,
    init?: RequestInit,
    validator?: ValidateFunction<T>,
  ): Promise<T> {
    if (this.config.requestDelegate) {
      const headers = new Headers(init?.headers);
      headers.set("Authorization", `Bearer ${token}`);
      return this.config.requestDelegate<T>(
        path,
        { ...init, headers },
        validator,
      );
    }

    const url = this.buildUrl(path);
    const headers = new Headers(init?.headers);

    headers.set("Authorization", `Bearer ${token}`);

    if (init?.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const response = await fetch(url, {
      ...init,
      headers,
    });

    if (!response.ok) {
      // Handle 402 Payment Required with structured error data
      if (response.status === 402) {
        const { message, data } = await this.parseErrorData(response);
        throw new PaymentRequiredError(message, data);
      }

      const errorMessage = await this.parseError(response);
      throw new ApiError(errorMessage, response.status, response.statusText);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    const data = await response.json();

    // Validate response if validator provided
    if (validator) {
      if (!validator(data)) {
        throw new ApiValidationError(
          "API response validation failed",
          validator.errors as ValidationError[],
        );
      }
    }

    return data as T;
  }
}
